import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";

function EditAdultStay(props) {
    const {
        adultStayToEdit, // the full object returned by your GET (with stayId, arrivaldate, etc.)
        updateAdultStay, // a function(payload, adultId, stayId) => does PUT
        adultId, // e.g. 52
        open,
        onClose,
        campuses,
    } = props;
    const [stay, setStay] = useState({
        residentialBase: "",
        arrivalDate: new Date(), // Date object for MUI DatePicker
        departureDate: new Date(),
        notes: "",
        overnightResident: false,
        gender: "",
    });

    useEffect(() => {
        if (!adultStayToEdit) return;

        const safeParse = (str) => {
            const d = new Date(str);
            return isNaN(d.getTime()) ? new Date() : d;
        };

        setStay({
            residentialBase: adultStayToEdit.residentialBase,
            arrivalDate: safeParse(adultStayToEdit.arrivaldate), // Date object
            departureDate: safeParse(adultStayToEdit.departuredate), // Date object
            notes: adultStayToEdit.notes,
            overnightResident: adultStayToEdit.overnightResident,
            gender: adultStayToEdit.gender || "",
        });
    }, [adultStayToEdit]);

    const handleSave = () => {
        if (!adultStayToEdit) return;

        // Build the payload exactly as your backend expects:
        const payload = {
            residentialBase: stay.residentialBase,
            arrivaldate: stay.arrivalDate.toISOString().slice(0, 10),
            departuredate: stay.departureDate.toISOString().slice(0, 10),
            notes: stay.notes,
            overnightResident: stay.overnightResident,
            gender: stay.overnightResident ? stay.gender : null,
        };

        console.log(adultStayToEdit.id);
        // Call the parent’s “updateAdultStay” with (payload, adultId, stayId):
        updateAdultStay(payload, adultStayToEdit.id);

        onClose();
    };

    const handleDateChange = (date, name) => {
        setStay((prevStay) => ({
            ...prevStay,
            [name]: date,
        }));
    };

    const handleCheckbox = (e) => {
        setStay((prev) => ({
            ...prev,
            overnightResident: e.target.checked,
        }));
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setStay((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Edit Stay</DialogTitle>
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack spacing={2} mt={1}>
                        <FormControl fullWidth>
                            <InputLabel id="campus-label">Campus</InputLabel>
                            <Select
                                labelId="campus-label"
                                id="campus"
                                name="residentialBase"
                                value={stay.residentialBase}
                                onChange={handleChange}
                                label="Campus"
                            >
                                {campuses.map((campus) => (
                                    <MenuItem
                                        key={campus.id}
                                        value={campus.name}
                                    >
                                        {campus.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <DatePicker
                            label="Arrive"
                            value={stay.arrivalDate}
                            onChange={(date) =>
                                handleDateChange(date, "arrivalDate")
                            }
                            renderInput={(params) => (
                                <TextField {...params} fullWidth />
                            )}
                        />

                        <DatePicker
                            label="Depart"
                            value={stay.departureDate}
                            onChange={(date) =>
                                handleDateChange(date, "departureDate")
                            }
                            renderInput={(params) => (
                                <TextField {...params} fullWidth />
                            )}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={stay.overnightResident}
                                    onChange={handleCheckbox}
                                    name="overnightResident"
                                    color="primary"
                                />
                            }
                            label="Overnight Resident?"
                        />

                        <FormControl
                            fullWidth
                            disabled={!stay.overnightResident}
                        >
                            <InputLabel id="gender-label">Gender</InputLabel>
                            <Select
                                labelId="gender-label"
                                id="gender"
                                name="gender"
                                value={stay.gender}
                                onChange={handleChange}
                            >
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                                <MenuItem value="Prefer not to say">
                                    Prefer not to say
                                </MenuItem>
                            </Select>
                            <FormHelperText>
                                This is only used for room allocation during
                                overnight stays.
                            </FormHelperText>
                        </FormControl>

                        <TextField
                            label="Notes"
                            name="notes"
                            variant="standard"
                            value={stay.notes}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Stack>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditAdultStay;
